/**
 * useKeyboardShortcuts - Keyboard shortcuts for the editor
 */
import { useEffect, useCallback } from 'react';

export function useKeyboardShortcuts({
  isPlaying,
  togglePlayPause,
  stepForward,
  stepBackward,
  goToStart,
  goToEnd,
  skipForward,
  skipBackward,
  undo,
  redo,
  splitAtPlayhead,
  deleteSelected,
  selectAll
}) {
  const handleKeyDown = useCallback((e) => {
    // Ignore if typing in an input
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
      return;
    }

    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    const cmdOrCtrl = isMac ? e.metaKey : e.ctrlKey;

    switch (e.key) {
      // Space - Play/Pause
      case ' ':
        e.preventDefault();
        togglePlayPause?.();
        break;

      // Arrow keys - Frame stepping / seeking
      case 'ArrowLeft':
        e.preventDefault();
        if (e.shiftKey) {
          skipBackward?.(1000); // 1 second
        } else {
          stepBackward?.(1); // 1 frame
        }
        break;

      case 'ArrowRight':
        e.preventDefault();
        if (e.shiftKey) {
          skipForward?.(1000); // 1 second
        } else {
          stepForward?.(1); // 1 frame
        }
        break;

      // Home/End - Go to start/end
      case 'Home':
        e.preventDefault();
        goToStart?.();
        break;

      case 'End':
        e.preventDefault();
        goToEnd?.();
        break;

      // J/K/L - Shuttle controls (like professional editors)
      case 'j':
        e.preventDefault();
        skipBackward?.(5000);
        break;

      case 'k':
        e.preventDefault();
        togglePlayPause?.();
        break;

      case 'l':
        e.preventDefault();
        skipForward?.(5000);
        break;

      // Cmd/Ctrl + Z - Undo
      case 'z':
        if (cmdOrCtrl) {
          e.preventDefault();
          if (e.shiftKey) {
            redo?.();
          } else {
            undo?.();
          }
        }
        break;

      // Cmd/Ctrl + Y - Redo (Windows style)
      case 'y':
        if (cmdOrCtrl) {
          e.preventDefault();
          redo?.();
        }
        break;

      // Cmd/Ctrl + B - Split at playhead
      case 'b':
        if (cmdOrCtrl) {
          e.preventDefault();
          splitAtPlayhead?.();
        }
        break;

      // Delete/Backspace - Delete selected
      case 'Delete':
      case 'Backspace':
        if (!cmdOrCtrl) {
          e.preventDefault();
          deleteSelected?.();
        }
        break;

      // Cmd/Ctrl + A - Select all
      case 'a':
        if (cmdOrCtrl) {
          e.preventDefault();
          selectAll?.();
        }
        break;

      default:
        break;
    }
  }, [
    togglePlayPause, stepForward, stepBackward, goToStart, goToEnd,
    skipForward, skipBackward, undo, redo, splitAtPlayhead, 
    deleteSelected, selectAll
  ]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
}
